import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { AppConfig } from '../config';
import { Link, RecentVideo, LinkType } from "../models/link";
import { createLinkUrl } from "./utils";
import api from "../plugins/api";

export const useLinkStore = defineStore('links', () => {
    const allLinks = ref<Link[]>([]);
    const channelLinks = ref<Link[]>([]);
    const recentLinks = ref<RecentVideo[]>([]);
    const likedVideos = ref<RecentVideo[]>([]);
    const amazonLinks = ref<Link[]>([]);
    const link = ref<Link>({} as Link);
    
    const isLoadingRecent = ref(false);
    const isLoadingLiked = ref(false);
    const isLoadingAmazon = ref(false);
    const isLoadingAll = ref(false);

    const sponsoredLinks = computed(() => {
      return allLinks.value.filter(el => el.type === LinkType.Sponsored);
    });

    async function fetchChannelLinks(channelId: string) {
      try {
        const { data } = await api.get(`/channels/${channelId}/links`);
        channelLinks.value = _convertResToLinks(data);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      }
    }

    async function fetchLink(linkId: string) {
      try {
        const { data } = await api.get(`/links/${linkId}`);
        link.value = _convertLinkToLink(data);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      }
    }

    async function updateLink(updatedLink: Link) {
      try {
        const reqData = {
          name: updatedLink.name,
          imageUrl: updatedLink.imageUrl,
          type: updatedLink.type,
          discount: updatedLink.discount.amount,
          code: updatedLink.discount.code
        };
          
        await api.patch(`/links/${updatedLink.id}`, reqData);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      }
    }

    async function fetchLinks() {
      isLoadingAll.value = true;
      try {
        const { data } = await api.get('/links');
        allLinks.value = _convertResToLinks(data);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      } finally {
        isLoadingAll.value = false;
      }
    }

    async function fetchRecentLinks(filters: LinkOpts) {
      isLoadingRecent.value = true;
      try {
        const { data } = await api.get('/links/recent', { params: filters });
        recentLinks.value = _convertLinksToRecentLinks(data as Link[]);
        recentLinks.value.sort((a, b) => {
          const ap = new Date(a.video.publishedAt || 0);
          const bp = new Date(b.video.publishedAt || 0);
          return ap > bp ? -1 : (ap < bp ? 1 : 0);
        });
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      } finally {
        isLoadingRecent.value = false;
      }
    }

    async function fetchAmazonAffiliateLinks(filters: LinkOpts) {
      isLoadingAmazon.value = true;
      try {
        const { data } = await api.get('/links/amazon', { params: filters });
        
        // Dedup links using the .id
        const links = data.filter((obj: Link, index: number, self: Link[]) =>
          index === self.findIndex((t: Link) => t.id === obj.id)
        );
        links.forEach((el: Link) => {
          el.imageUrl = el.imageUrl || `https://www.google.com/s2/favicons?sz=64&domain=${el.domain || el.resolvedUrl || el.url}`;
        });
        amazonLinks.value = links;
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      } finally {
        isLoadingAmazon.value = false;
      }
    }

    async function deleteLink(linkToDelete: Link) {
      try {
        await api.delete(`/links/${linkToDelete.id}`);
        await fetchChannelLinks(linkToDelete.channel.id);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      }
    }

    async function fetchLikedVideos() {
      isLoadingLiked.value = true;
      try {
        const { data } = await api.get('/youtube/likes');
        likedVideos.value = _convertVideosToRecentLinks(data as any[]);
      } catch (error: any) {
        // notificationStore.showError handled by api interceptor
      } finally {
        isLoadingLiked.value = false;
      }
    }

    function reset() {
      allLinks.value = [];
      channelLinks.value = [];
      recentLinks.value = [];
      amazonLinks.value = [];
      link.value = {} as Link;
    }

    function _convertResToLinks(res: LinkRes[]): Link[] {
      return res.map((el: LinkRes) => ({
        id: el.linkId,
        url: createLinkUrl(AppConfig.DOMAIN, el.url, el.linkId),
        resolvedUrl: el.resolvedUrl,
        type: el.linkType,
        videoCount: 1,
        imageUrl: el.imageUrl || `https://www.google.com/s2/favicons?sz=64&domain=${el.domain}`,
        name: el.name || el.resolvedUrl || el.url,
        domain: el.domain,
        channel: {
            id: el.channelId,
            name: el.channelName,
            imageUrl: el.channelPictureUrl
        },
        discount: {
            amount: el.discount,
            code: el.code
        },
        isSponsored: el.isSponsored
      }));
    }

    function _convertLinksToRecentLinks(res: Link[]): RecentVideo[] {
      const videos: Record<string, Link[]> = {};
      const results: RecentVideo[] = [];
      
      for (const linkItem of res) {
        if (!linkItem.video) break;

        const video = linkItem.video;
        if (videos[video.id]) {
          videos[video.id].push(linkItem);
        } else {
          videos[video.id] = [linkItem];
        }
      }

      for (const videoId in videos) {
        videos[videoId].forEach((l: Link) => { 
          l.imageUrl = l.imageUrl || `https://www.google.com/s2/favicons?sz=64&domain=${l.domain}`;
          l.name = l.name || l.resolvedUrl || l.url;
          l.url = createLinkUrl(AppConfig.DOMAIN, l.url, l.id);
          l.video!.publishedAt = new Date(l.video?.publishedAt || '').toLocaleDateString();
        });
        
        const links: Link[] = videos[videoId];
        if (!links[0].video) break;

        links.sort((a: Link, b: Link) => {
          if ((a.videoCount > b.videoCount) || (a.type !== LinkType.Sponsored && b.type === LinkType.Sponsored)) {
            return -1;
          }
          if ((a.videoCount < b.videoCount) || (a.type === LinkType.Sponsored && b.type !== LinkType.Sponsored)) {
            return 1;
          }
          return 0;
        });

        const video = links[0].video;
        results.push({
          video: { 
            id: video.id,
            title: video.title,
            thumbnailUrl: video.thumbnailUrl,
            publishedAt: video.publishedAt,
            ytId: video.yt_video_id
          },
          links: links
        });
      }

      return results;
    }

    function _convertVideosToRecentLinks(res: any[]): RecentVideo[] {
      return res.map((v: any) => ({
        video: {
          id: v.ID,
          title: v.Title,
          thumbnailUrl: v.ThumbnailURL,
          publishedAt: new Date(v.LikedAt || v.PublishedAt).toLocaleDateString(),
          ytId: v.VideoID
        },
        links: (v.Links || []).map((l: any) => ({
          id: l.ID,
          url: createLinkUrl(AppConfig.DOMAIN, l.URL, l.ID),
          resolvedUrl: l.ResolvedURL,
          type: l.Type,
          imageUrl: l.ImageURL || `https://www.google.com/s2/favicons?sz=64&domain=${l.Domain}`,
          name: l.Name || l.ResolvedURL || l.URL,
          domain: l.Domain,
          discount: {
            amount: l.Discount,
            code: l.Code
          }
        }))
      }));
    }

    function _convertLinkToLink(linkData: any): Link {
      return {
        id: linkData.id,
        url: linkData.url,
        imageUrl: linkData.imageUrl,
        name: linkData.name,
        type: linkData.type,
        videoCount: linkData.linkVideoCount,
        video: {
          title: linkData.ytVideoName,
          thumbnailUrl: linkData.ytVideoThumbUrl
        },
        discount: {
          code: linkData.code,
          amount: linkData.discount
        },
        isQuickLink: linkData.isQuickLink
      } as Link;
    }
  
    return {
      link, allLinks, amazonLinks, sponsoredLinks, channelLinks, recentLinks, likedVideos,
      isLoadingRecent, isLoadingLiked, isLoadingAmazon, isLoadingAll,
      fetchChannelLinks, fetchLinks, fetchRecentLinks, fetchAmazonAffiliateLinks, fetchLikedVideos, fetchLink, updateLink, deleteLink, reset 
    };
});

export interface LinkRes {
  channelId: string;
  channelName: string;
  channelPictureUrl: string;
  youtubeId: string;
  quickLinks: Link[];
  linkId: string;
  url: string;
  resolvedUrl?: string;
  linkType: string;
  name: string;
  domain: string;
  discount: string;
  code: string;
  imageUrl: string;
  isSponsored: Boolean;
}

export interface LinkOpts {
  userId?: string;
  channelId?: string;
  publishedAfter?: string;
  limit?: number; 
}